import React, { useState, useEffect } from "react";
import ContactForm from "../components/contact/ContactForm";
import ContactInfo from "../components/contact/ContactInfo";
import MapSection from "../components/contact/MapSection";
import PageContainer from "../components/layout/PageContainer";

const Contact = () => {
  // Add window size tracking for responsive design
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Define breakpoints for responsive design
  const isMobile = windowWidth <= 768;
  const isSmallScreen = windowWidth <= 480;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get responsive styles based on window size
  const getResponsiveStyles = () => {
    return {
      heroSection: {
        height: isSmallScreen ? "30vh" : isMobile ? "35vh" : "40vh",
        padding: isSmallScreen
          ? "30px 15px"
          : isMobile
          ? "40px 20px"
          : "0 30px",
      },
      heroContent: {
        padding: isSmallScreen ? "0 15px" : isMobile ? "0 20px" : "0 30px",
        maxWidth: isSmallScreen ? "100%" : isMobile ? "95%" : "900px",
      },
      heroTitle: {
        fontSize: isSmallScreen ? "2.2rem" : isMobile ? "2.8rem" : "3.5rem",
        marginBottom: isSmallScreen ? "15px" : "20px",
      },
      heroSubtitle: {
        fontSize: isSmallScreen ? "1rem" : isMobile ? "1.1rem" : "1.2rem",
        maxWidth: isSmallScreen ? "100%" : isMobile ? "90%" : "700px",
      },
      contactLayout: {
        gridTemplateColumns:
          isSmallScreen || isMobile
            ? "1fr"
            : "repeat(auto-fit, minmax(300px, 1fr))",
        gap: isSmallScreen ? "30px" : "40px",
        marginBottom: isSmallScreen ? "40px" : "60px",
        padding: isSmallScreen ? "0 10px" : "0",
      },
      infoSection: {
        order: isMobile ? 2 : 1, // Move info below form on mobile
      },
      formSection: {
        order: isMobile ? 1 : 2,
      },
      mapContainer: {
        marginBottom: isSmallScreen ? "40px" : "60px",
        padding: isSmallScreen ? "0 10px" : "0",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <div style={styles.pageWrapper}>
      {/* Hero Section */}
      <section
        style={{ ...styles.heroSection, ...responsiveStyles.heroSection }}
      >
        <div style={styles.heroOverlay}>
          <div style={styles.gridPattern}></div>
        </div>

        <div style={{ ...styles.heroContent, ...responsiveStyles.heroContent }}>
          <h1 style={{ ...styles.heroTitle, ...responsiveStyles.heroTitle }}>
            Get In <span style={styles.accentText}>Touch</span>
          </h1>
          <div style={styles.titleDivider}></div>
          <p
            style={{ ...styles.heroSubtitle, ...responsiveStyles.heroSubtitle }}
          >
            We're here to answer any questions about our elevator solutions and
            services. Reach out to our team today.
          </p>
        </div>
      </section>

      <PageContainer>
        <div
          style={{ ...styles.contactLayout, ...responsiveStyles.contactLayout }}
        >
          {/* Contact Information */}
          <div
            style={{ ...styles.infoSection, ...responsiveStyles.infoSection }}
          >
            <ContactInfo isMobile={isMobile} isSmallScreen={isSmallScreen} />
          </div>

          {/* Contact Form */}
          <div
            style={{ ...styles.formSection, ...responsiveStyles.formSection }}
          >
            <ContactForm isMobile={isMobile} isSmallScreen={isSmallScreen} />
          </div>
        </div>

        {/* Map Section */}
        <section
          style={{ ...styles.mapContainer, ...responsiveStyles.mapContainer }}
        >
          <MapSection isMobile={isMobile} isSmallScreen={isSmallScreen} />
        </section>
      </PageContainer>
    </div>
  );
};

const styles = {
  pageWrapper: {
    backgroundColor: "#ffffff",
    
  },
  heroSection: {
    height: "40vh",
    minHeight: "300px",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundImage:
      "linear-gradient(135deg, #01162E 0%, #052447 45%, #0A3A70 100%)",
    color: "white",
    marginBottom: "50px",
  },
  heroOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background:
      "radial-gradient(circle at 30% 40%, rgba(20, 90, 170, 0.15) 0%, rgba(10, 50, 120, 0) 70%)",
    zIndex: 1,
  },
  gridPattern: {
    position: "absolute",
    width: "100%",
    height: "100%",
    backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px)
        `,
    backgroundSize: "30px 30px",
    opacity: 0.6,
  },
  heroContent: {
    marginTop: "60px",
    position: "relative",
    zIndex: 2,
    maxWidth: "900px",
    padding: "0 30px",
    textAlign: "center",
  },
  heroTitle: {
    fontSize: "3.5rem",
    fontWeight: 800,
    marginBottom: "20px",
    fontFamily: "'Inter', sans-serif",
  },
  accentText: {
    color: "#4F9DF3",
    textShadow: "0 0 15px rgba(30, 120, 230, 0.3)",
  },
  titleDivider: {
    height: "3px",
    width: "80px",
    background:
      "linear-gradient(90deg, rgba(255,255,255,0), #4F9DF3, rgba(255,255,255,0))",
    margin: "0 auto 25px",
    borderRadius: "2px",
  },
  heroSubtitle: {
    fontSize: "1.2rem",
    fontWeight: 300,
    maxWidth: "700px",
    margin: "0 auto",
    lineHeight: 1.6,
    color: "rgba(255, 255, 255, 0.9)",
  },
  contactLayout: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "40px",
    marginBottom: "60px",
  },
  infoSection: {
    minWidth: "300px",
  },
  formSection: {
    flex: "1",
  },
  mapContainer: {
    marginBottom: "60px",
  },
};

export default Contact;
